import { CloseOutlined, SearchOutlined, UserOutlined } from '@ant-design/icons';
import { Avatar, Input } from 'antd';
import { useEffect, useRef, useState } from 'react';
import { getUserProfilePictureUrl } from '../../../common/userHelper';
import colorTokens from '../../../constants/design_system/colors';
import { User } from '../../../types/User';
import CustomTooltip from '../Tooltip/CustomTooltip';
import BaseDropdown from './BaseDropdown';
import './ItemDropdown.scss';
import NoDragWrapper from './NoDragWrapper';

export type OwnerDropdownProps = {
  owner: User | null;
  owners: User[];
  onSelect?: (owner?: User) => void;
  onBlur?: () => void;
  disabled?: boolean;
  size: 'small' | 'medium' | 'large';
  hideOutline?: boolean;
  defaultOpen?: boolean;
  hideRemove?: boolean;
  /** Wording for the clear row. Defaults to "Remove Owner", which is wrong wherever the
   *  dropdown picks something other than a task's owner. */
  removeLabel?: string;
  selectedElement?: React.ReactNode;
  emptyElement?: React.ReactNode;
  tooltipTitle?: string;
  className?: string;
  position?: 'bottom' | 'bottomLeft' | 'bottomRight' | 'topCenter' | 'topLeft' | 'topRight';
};

const OwnerDropdown = (props: OwnerDropdownProps) => {
  const [isDropdownOpen, setDropdownOpen] = useState(props.defaultOpen || false);
  const [scrollKey, setScrollKey] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const searchInputRef = useRef<any>(null);

  // Auto-focus search input when dropdown opens
  useEffect(() => {
    if (isDropdownOpen && searchInputRef.current) {
      // Small delay to ensure dropdown is fully rendered
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 0);
    }
  }, [isDropdownOpen]);

  const filteredOwners = props.owners
    .filter((owner) => (owner.fullName || owner.email)?.toLowerCase().includes(searchTerm.toLowerCase()) ?? false)
    .sort((a, b) => {
      const userA = a.fullName || a.email;
      const userB = b.fullName || b.email;

      if (!userA && !userB) return 0;
      if (!userA) return 1;
      if (!userB) return -1;

      return userA.localeCompare(userB);
    });

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const onSelect = (owner: User) => {
    if (props.onSelect) {
      props.onSelect(owner);
    }
    isDropdownOpen && setDropdownOpen(false);
  };

  return (
    <NoDragWrapper className="h-full">
      <CustomTooltip title={props.tooltipTitle}>
        <BaseDropdown
          disableTransition
          trigger={['click']}
          open={isDropdownOpen}
          disabled={props.disabled}
          placement={props.position || 'bottomLeft'}
          onOpenChange={(open) => {
            if (open) setScrollKey((k) => k + 1);
            setDropdownOpen(open);
            if (!open && props.onBlur) {
              props.onBlur();
            }
          }}
          dropdownRender={() => (
            <div
              key={scrollKey}
              // Fixed width so the dropdown doesn't resize between "no results" (no
              // scrollbar) and "with results" (scrollbar reserves ~15px on the right,
              // shrinking the content box) — matches the search input's 200px + p-2 padding.
              className="flex flex-col items-start justify-start gap-1 bg-brand-white shadow-md rounded-lg p-2 w-[216px] max-h-[500px] overflow-y-auto"
            >
              {/* Search input */}
              <div className="w-full mb-2">
                <Input
                  ref={searchInputRef}
                  placeholder="Search for member"
                  prefix={<SearchOutlined className="mr-1" />}
                  allowClear={searchTerm.length > 0}
                  className="[&_input::placeholder]:!text-neutral-900 px-2 py-1 text-neutral-900 mr-auto w-[200px]"
                  value={searchTerm}
                  onChange={handleSearch}
                  onClick={(e) => e.stopPropagation()}
                  size="small"
                />
              </div>

              {filteredOwners.map((owner) => (
                <div key={owner.id} className="w-[192px] cursor-pointer" onClick={() => onSelect(owner)}>
                  <OwnerLabel user={owner} size="large" />
                </div>
              ))}

              {filteredOwners.length === 0 && (
                <div className="w-[200px] justify-start text-neutral-900 text-xs font-semibold">No results found.</div>
              )}

              {props.owner && props.onSelect && !props.hideRemove && (
                <div className="w-full flex flex-col items-start justify-start mt-1">
                  <div className="w-full h-px bg-neutral-300"></div>
                  <div
                    className="w-full px-1 py-2 text-neutral-900 text-sm cursor-pointer hover:bg-neutral-200 flex items-center gap-2"
                    onClick={() => {
                      if (props.onSelect) {
                        props.onSelect(undefined);
                        setDropdownOpen(false);
                      }
                    }}
                  >
                    <CloseOutlined className="text-neutral-900" />
                    {props.removeLabel ?? 'Remove Owner'}
                  </div>
                </div>
              )}
            </div>
          )}
        >
          <div
            // A disabled dropdown that still shows a pointer reads as clickable and then
            // does nothing on click, which is the one thing a disabled control must not do.
            className={`${props.className ?? 'h-full'} flex items-center px-1 ${
              props.disabled ? 'cursor-not-allowed' : 'cursor-pointer'
            }`}
            style={{ outline: isDropdownOpen && !props.hideOutline ? `2px solid ${colorTokens.green[300]}` : 'none' }}
          >
            {props.selectedElement ? (
              props.selectedElement
            ) : props.owner ? (
              <div className="relative owner">
                <OwnerLabel user={props.owner} size={props.size} />
              </div>
            ) : props.emptyElement ? (
              props.emptyElement
            ) : (
              <div className="w-full h-full flex items-center justify-center gap-1">
                <Avatar
                  size={16}
                  className="bg-neutral-200"
                  icon={<UserOutlined style={{ color: colorTokens.neutral[800] }} />}
                />
                <div className="text-neutral-900 text-sm font-normal">Owner</div>
              </div>
            )}
          </div>
        </BaseDropdown>
      </CustomTooltip>
    </NoDragWrapper>
  );
};

const OwnerLabel = (props: { user: User; size: 'small' | 'medium' | 'large' }) => {
  return (
    <div className="flex items-center gap-1 hover:bg-neutral-100 rounded-md p-1 w-full min-w-0">
      <Avatar
        key={props.user.id}
        size={24}
        src={getUserProfilePictureUrl(props.user.profilePicturePath)}
        className="flex justify-center items-center bg-neutral-200 shrink-0"
      >
        <div className="text-center text-slate-500 text-[12px] font-semibold font-['Figtree'] leading-3">
          {(props.user.fullName || props.user.email)?.slice(0, 1)}
        </div>
      </Avatar>
      {(props.size === 'medium' || props.size === 'large') && (
        <span className="max-w-[250px] truncate text-sm font-normal text-neutral-900 leading-4" key={props.user.email}>
          {props.user.fullName || props.user.email}
        </span>
      )}
    </div>
  );
};

export default OwnerDropdown;
